import { useState } from "react"
import axios from "axios"
import { useLogger, useGlobalLoader } from "src/providers"

interface DataSource {
  id: string
  status: string
}

interface UseDataSourcesSelectionProps {
  dataSources: DataSource[]
  dataSourceFilter: any
  onDataRefresh: () => void
}

export const useDataSourcesSelection = ({
  dataSources,
  dataSourceFilter,
  onDataRefresh
}: UseDataSourcesSelectionProps) => {
  const { logger } = useLogger()
  const { showLoader, hideLoader } = useGlobalLoader()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(dataSources.map((source) => source.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id)
      } else {
        return [...prev, id]
      }
    })
  }

  const clearSelection = () => {
    setSelectedIds([])
    setSuccessMessage(null)
  }

  const isSelected = (id: string) => selectedIds.includes(id)
  const isAllSelected = dataSources.length > 0 && selectedIds.length === dataSources.length
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < dataSources.length

  // Check if any selected record is deletable (not deleted)
  const hasDeletableRecords = selectedIds.some((id) => {
    const source = dataSources.find((s) => s.id === id)
    return source && source.status.toUpperCase() !== "DELETED"
  })

  // Check if all selected records are in PENDING state
  const allSelectedArePending =
    selectedIds.length > 0 &&
    selectedIds.every((id) => {
      const source = dataSources.find((s) => s.id === id)
      return source && source.status.toUpperCase() === "PENDING"
    })

  const handleBulkDelete = () => {
    // Filter out deleted records
    const deletableIds = selectedIds.filter((id) => {
      const source = dataSources.find((s) => s.id === id)
      return source && source.status.toUpperCase() !== "DELETED"
    })

    if (deletableIds.length === 0) {
      return
    }

    showLoader()
    Promise.all(deletableIds.map((id) => axios.delete(`/api/km/ingest/document/${id}`)))
      .then(() => {
        const message =
          deletableIds.length === 1
            ? "Your source file has been successfully deleted."
            : `Your ${deletableIds.length} source files have been successfully deleted.`
        setSuccessMessage(message)
        setSelectedIds([])
        onDataRefresh()

        // Auto-hide the success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null)
        }, 5000)
      })
      .catch((e) => {
        logger.error("System - Failed to delete data sources", e)
      })
      .finally(() => {
        hideLoader()
      })
  }

  const handleBulkApprove = () => {
    showLoader()
    Promise.all(selectedIds.map((id) => axios.post(`/api/km/ingest/document/${id}/approve`)))
      .then(() => {
        const communityName = dataSourceFilter?.label || "Community"
        const message =
          selectedIds.length === 1
            ? `1 approved source has been added to the ${communityName}.`
            : `${selectedIds.length} approved sources have been added to the ${communityName}.`
        setSuccessMessage(message)
        setSelectedIds([])
        onDataRefresh()

        // Auto-hide the success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null)
        }, 5000)
      })
      .catch((e) => {
        logger.error("System - Failed to approve data sources", e)
      })
      .finally(() => {
        hideLoader()
      })
  }

  return {
    selectedIds,
    setSelectedIds,
    successMessage,
    handleSelectAll,
    handleSelectOne,
    clearSelection,
    isSelected,
    isAllSelected,
    isSomeSelected,
    hasDeletableRecords,
    allSelectedArePending,
    handleBulkDelete,
    handleBulkApprove
  }
}
